import { Link, useLocation } from 'react-router-dom'
import { useState, useEffect, useContext } from 'react'
import { useUserAssignment } from '@/lib/useUserAssignment'
import { usePermissionLoading, PermissionContext } from '@/hooks/usePermission'
import { evaluatePathAccess, canAccessPath, type AccessUser } from '@/lib/routeAccess'
import { brand } from '#modules/branding'
import { createClient } from '@/lib/supabase/client'
import { useSignedUrl } from '@/lib/services/storage'
import { useUnsignedCounts } from '@/lib/useUnsignedPCRCount'
import { useChatUnread } from '@/hooks/useChatUnread'
import { useUnreadNotificationCount } from '@/hooks/useUnreadNotificationCount'
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { SidebarIcon, RAINBOW_ICON_COLORS } from './SidebarIcons'
import { useTheme } from './ThemeProvider'
import { BadgePopover } from './BadgePopover'

// -------------------------------------------------------------------------------------------------
// Nav Registry
// -------------------------------------------------------------------------------------------------

type SubItem = {
  label: string;
  href: string;
  navState?: Record<string, unknown>
  preserveSub?: boolean // preserve sub item when promoting to header, omit = true
  updateLabel?: boolean // update label when promoting to header, omit = false
}

type NavItem = {
  id: string;
  icon: string;
  label: string;
  href?: string;
  navState?: Record<string, unknown>
  subItems?: SubItem[];
}

// Resolve step produces undefined href if unresolvable.
type ResolvedSubItem = Omit<SubItem, 'href'> & { href?: string }
type ResolvedNavItem = Omit<NavItem, 'href' | 'subItems'> & {
  href?: string
  subItems?: ResolvedSubItem[]
}

// Pre render filter excludes undefined/inaccessible hrefs.
type RenderSubItem = ResolvedSubItem & { href: string }
type RenderNavItem = Omit<ResolvedNavItem, 'href' | 'subItems'> & {
  href: string
  subItems: RenderSubItem[]
}

/** Sortable nav items rendered in main sidebar */
const NAV: NavItem[] = [
  {
    id: 'home',
    icon: 'home',
    label: 'My Assignment',
    href: '/dashboard/my-unit',
  },
  {
    id: 'incidents',
    icon: 'incidents',
    label: 'Incidents',
    subItems: [
      { label: 'Incident Details', href: '/incidents/{userincident}', preserveSub: false, updateLabel: true },
      { label: 'All Incidents', href: '/incidents' },
      { label: 'Closed Incidents', href: '/incidents?status=Closed' },
      { label: 'New Incident', href: '/incidents/new' },
      { label: 'ICS 214 Logs', href: '/ics214' },
      { label: 'New ICS 214', href: '/ics214/new' },
    ],
  },
  {
    id: 'encounters',
    icon: 'encounters',
    label: 'Encounters',
    href: '/encounters',
    subItems: [
      { label: 'New Encounter', href: '/encounters/new' },
      { label: 'Unsigned Items', href: '/unsigned-items' },
      { label: 'MAR', href: '/mar' },
      { label: 'New MAR Entry', href: '/mar/new' },
    ],
  },
  {
    id: 'units',
    icon: 'units',
    label: 'Units',
    subItems: [
      { label: 'Unit Details', href: '/units/{userunit}', navState: { 'showList': false }, preserveSub: false, updateLabel: true },
      { label: 'All Units', href: '/units' },
      { label: 'Add New Unit', href: '/units/new' },
      { label: 'Daily Checklist', href: '/cs/checklist' },
    ],
  },
  {
    id: 'inventory',
    icon: 'inventory',
    label: 'Inventory',
    href: '/inventory',
    subItems: [
      { label: 'Item Catalog', href: '/catalog' },
      { label: 'Add Inventory', href: '/inventory/add' },
      { label: 'Formulary Templates', href: '/formulary' },
      { label: 'Reorder Report', href: '/inventory/reorder' },
      { label: 'Burn Rate', href: '/inventory/burnrate' },
      { label: 'Expiration Dashboard', href: '/inventory/expiring' },
      { label: 'Itemized Billing', href: '/billing' },
    ],
  },
  {
    id: 'cs',
    icon: 'cs',
    label: 'CS System',
    subItems: [
      { label: 'CS Overview', href: '/cs' },
      { label: 'Receive CS', href: '/cs/receive' },
      { label: 'Transfer CS', href: '/cs/transfer' },
      { label: 'Daily Count', href: '/cs/count' },
      { label: 'Audit Log', href: '/cs/audit' },
    ],
  },
  {
    id: 'supply',
    icon: 'supply',
    label: 'Supply Runs',
    href: '/supply-runs',
    subItems: [
      { label: 'New Supply Run', href: '/supply-runs/new' },
    ],
  },
  {
    id: 'roster',
    icon: 'roster',
    label: 'Employee Roster',
    href: '/roster',
    subItems: [
      { label: 'HR Credentials', href: '/roster/hr' },
    ],
  },
  {
    id: 'chat',
    icon: 'chat',
    label: 'Team Chat',
    href: '/chat',
  },
  {
    id: 'payroll',
    icon: 'payroll',
    label: 'Payroll',
    subItems: [
      { label: 'Overview', href: '/payroll' },
      { label: 'My Payroll', href: '/payroll/my', preserveSub: false, updateLabel: true },
    ]
  },
  {
    id: 'map',
    icon: 'map',
    label: 'Live Map',
    href: '/map',
  },
  {
    id: 'admin',
    icon: 'admin',
    label: 'Admin',
    href: '/admin',
    subItems: [
      { label: 'Announcements & Push', href: '/admin/announcements' },
      { label: 'AI Requests', href: '/admin/chat-requests' },
      { label: 'Company Profile', href: '/admin/company' },
      { label: 'Analytics', href: '/analytics' },
      { label: 'Financial', href: '/admin/financial' },
      { label: 'Pay Rates', href: '/roster/pay-rates' },
      { label: 'External Dashboard', href: '/admin/fire-dashboard' },
      { label: 'Schedule', href: '/schedule' },
      { label: 'Coverage Calendar', href: '/schedule/calendar' },
      { label: 'Generate Schedule', href: '/schedule/generate' },
      { label: 'Contacts', href: '/contacts' },
      { label: 'Roles & Permissions', href: '/admin/roles' },
    ],
  },
]

/** non-sortable nav items rendered in the sidebar footer.
 * Notifications is excluded - it renders as a special badge widget in the employee row. */
const FOOTER_NAV: NavItem[] = [
  {
    id: 'documents',
    icon: 'documents',
    label: 'Policies & Procedures',
    href: '/documents'
  },
  {
    id: 'profile',
    icon: 'profile',
    label: 'My Profile',
    href: '/profile'
  },
  {
    id: 'schedule-request',
    icon: 'schedule',
    label: 'Schedule Request',
    href: '/schedule/request'
  },
]

/** href variable formatter */
function resolveHref(href: string, user: AccessUser): string | undefined {
  const vars: Record<string, string | null | undefined> = {
    userunit:     user.incidentUnit?.unit_id,
    userincident: user.incidentUnit?.incident_id,
  }
  let missing = false
  const resolved = href.replace(/\{(\w+)\}/g, (_, key: string) => {
    const val = vars[key]
    if (!val) { missing = true; return '' }
    return val
  })
  return missing ? undefined : resolved
}

// -------------------------------------------------------------------------------------------------
// Nav Sorting
// -------------------------------------------------------------------------------------------------

const STORAGE_KEY = 'firepcr-sidebar-order'

/** Returns `ids` in the user's saved order. Always returns exactly the input set — no additions, no removals. */
function loadSidebarOrder(ids: string[]): string[] {
  if (typeof window === 'undefined') return ids
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') as string[]
    const merged = saved.filter(id => ids.includes(id))
    // Insert new ids after their last known predecessor or in coded order
    ids.forEach((id, idx) => {
      if (!merged.includes(id)) {
        const insertAfter = ids.slice(0, idx).reverse().find(i => merged.includes(i))
        if (insertAfter !== undefined) merged.splice(merged.indexOf(insertAfter) + 1, 0, id)
        else merged.splice(idx, 0, id)
      }
    })
    return merged
  } catch {
    // return coded order on error
    return ids
  }
}

function storeSidebarOrder(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
  } catch {}
}

// -------------------------------------------------------------------------------------------------
// Current Selection
// -------------------------------------------------------------------------------------------------

type ActiveMatch = { navId: string; subHref: string | null }

// Finds the single active nav item for the current location.
// Returns { navId, subHref }
//
// Longest prefix match wins. Exact matches win naturally (they are the longest possible match).
// Sub-items checked before item href so ties resolve to sub-item (subHref gets set).
function findActiveMatch(items: ResolvedNavItem[], location: string): ActiveMatch | null {
  let best: ActiveMatch | null = null
  let bestLen = 0
  for (const item of items) {
    for (const sub of item.subItems ?? []) {
      if (sub.href && location.startsWith(sub.href) && sub.href.length > bestLen) {
        best = { navId: item.id, subHref: sub.href }
        bestLen = sub.href.length
      }
    }
    if (item.href && location.startsWith(item.href) && item.href.length > bestLen) {
      best = { navId: item.id, subHref: null }
      bestLen = item.href.length
    }
  }
  return best
}

// ── Sortable nav item ────────────────────────────────────────────────────────
function SortableNavItem({
  item, activeMatch, expanded, toggle, onNavigate, badges, isRainbow, sidebarText, chatUnread,
}: {
  item: RenderNavItem
  activeMatch: ActiveMatch | null
  expanded: string | null
  toggle: (label: string) => void
  onNavigate?: () => void
  badges?: Record<string, { charts: number; notes: number; mar: number; total: number }>
  isRainbow?: boolean
  sidebarText?: { inactive: string; muted: string }
  chatUnread?: number
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const isActive = activeMatch?.navId === item.id
  const isExpanded = expanded === item.id

  // Icon color: rainbow = per-icon color always, others = primary when active
  const iconColor = isRainbow
    ? (RAINBOW_ICON_COLORS[item.icon] || 'var(--color-primary, #dc2626)')
    : (isActive ? 'var(--color-primary, #dc2626)' : undefined)

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center group">
        {/* Drag handle — subtle grip, only visible on hover */}
        <div
          {...attributes}
          {...listeners}
          className="pl-1.5 pr-0.5 py-3 cursor-grab active:cursor-grabbing text-gray-700 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity touch-none select-none"
          title="Drag to reorder"
        >
          <span aria-hidden="true">⠿</span>
        </div>

        <Link
          to={item.href}
          state={item.navState}
          onClick={onNavigate}
          aria-current={isActive ? 'page' : undefined}
          className={`flex-1 flex items-center gap-3 pl-2.5 py-2.5 text-sm font-medium rounded-lg mx-1 transition-all duration-150 ${
            isActive
              ? 'text-white'
              : 'hover:bg-white/[0.04]'
          }`}
          style={{
            color: isActive ? '#fff' : (sidebarText?.inactive || 'var(--color-text-muted, #9ca3af)'),
            ...(isActive ? {
              backgroundColor: 'color-mix(in srgb, var(--color-primary, #374151) 15%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-primary, #374151) 30%, transparent)',
            } : {}),
          }}
        >
          <span aria-hidden="true" className={`w-6 h-6 shrink-0 flex items-center justify-center transition-colors ${isActive || isRainbow ? 'opacity-100' : 'opacity-50'}`} style={iconColor ? { color: iconColor } : {}}>
            <SidebarIcon name={item.icon} />
          </span>
          <span>{item.label}</span>
          {badges && badges[item.id] && badges[item.id].total > 0 && (
            <BadgePopover badge={badges[item.id]} />
          )}
          {item.icon === 'chat' && chatUnread && chatUnread > 0 ? (
            <>
              <span aria-hidden="true" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none">
                {chatUnread > 99 ? '99+' : chatUnread}
              </span>
              <span className="sr-only">, {chatUnread} unread</span>
            </>
          ) : null}
        </Link>
        {item.subItems.length > 0 && (
          <button
            onClick={() => toggle(item.id)}
            aria-label={`Toggle ${item.id} sub-menu`}
            aria-expanded={isExpanded}
            aria-controls={`nav-submenu-${item.id}`}
            className="pr-4 py-3 text-gray-600 hover:text-gray-400 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </button>
        )}
      </div>

      {isExpanded && (
        <div
          id={`nav-submenu-${item.id}`}
          role="group"
          aria-label={`${item.id} sub-menu`}
          className="ml-[42px] mr-3 mb-1 pl-2 border-l border-white/[0.06] rounded-bl-lg"
        >
          {item.subItems.map(sub => {
            const isSubHere = sub.href === activeMatch?.subHref
            return (
              <Link
                key={sub.href}
                to={sub.href}
                state={sub.navState}
                onClick={onNavigate}
                aria-current={isSubHere ? 'page' : undefined}
                className={`block py-1.5 px-3 text-[13px] rounded-md transition-all duration-150 ${
                  isSubHere ? '' : 'hover:bg-white/[0.04]'
                }`}
                style={isSubHere ? { color: "var(--color-primary, #f87171)" } : { color: sidebarText?.muted || "var(--color-text-muted, #6b7280)" }}
              >
                {sub.label}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

type OrgBranding = { name: string; dba: string | null; logo_url: string | null }

// Detect if sidebar needs light text (dark sidebar bg on light-page themes like Patriot)
function getSidebarTextColors(): { inactive: string; muted: string } {
  if (typeof document === 'undefined') return { inactive: 'var(--color-text-muted, #9ca3af)', muted: 'var(--color-text-muted, #6b7280)' }
  const sidebarBg = getComputedStyle(document.documentElement).getPropertyValue('--color-sidebar-bg').trim()
  if (!sidebarBg || !sidebarBg.startsWith('#') || sidebarBg.length < 7) return { inactive: 'var(--color-text-muted, #9ca3af)', muted: 'var(--color-text-muted, #6b7280)' }
  const hex = sidebarBg.replace('#', '')
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  if (luminance < 0.45) return { inactive: '#b0b0c8', muted: '#8080a0' }
  return { inactive: 'var(--color-text-muted, #9ca3af)', muted: 'var(--color-text-muted, #6b7280)' }
}

// ── Main sidebar ─────────────────────────────────────────────────────────────
export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()
  const pathname = location.pathname
  const search = location.search
  const permLoading = usePermissionLoading()
  const { permissions } = useContext(PermissionContext)
  const assignment = useUserAssignment()
  const unsignedCounts = useUnsignedCounts()
  const { totalUnread: chatUnread } = useChatUnread()
  const { count: notifyCount } = useUnreadNotificationCount()
  const { theme } = useTheme()
  const isRainbow = theme.preset === 'rainbow'
  const sidebarText = getSidebarTextColors()
  const [org, setOrg] = useState<OrgBranding | null>(null)
  const orgLogoUrl = useSignedUrl(createClient(), org?.logo_url)
  const accessUser: AccessUser = { incidentUnit: assignment.incidentUnit ?? null, permissions }

  // Load organization data
  useEffect(() => {
    const supabase = createClient()
    supabase
      .from('organizations')
      .select('name, dba, logo_url')
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) setOrg(data as unknown as OrgBranding)
      })
  }, [])

  // Resolve hrefs for all NAV items
  const resolvedNav = NAV.map(nav => ({
    ...nav,
    href: nav.href ? resolveHref(nav.href, accessUser) : undefined,
    subItems: (nav.subItems ?? []).map(sub => ({
      ...sub,
      href: sub.href ? resolveHref(sub.href, accessUser) : undefined
    }))
  }))

  // Find active nav item/sub item, including footer
  const activeMatch = findActiveMatch([...resolvedNav, ...FOOTER_NAV], pathname + search)

  // Load user sidebar ordering
  const [navOrder, setOrder] = useState<string[]>(loadSidebarOrder(NAV.map(n => n.id)))
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = navOrder.indexOf(active.id as string)
    const newIndex = navOrder.indexOf(over.id as string)
    const newOrder = arrayMove(navOrder, oldIndex, newIndex)
    setOrder(newOrder)
    storeSidebarOrder(newOrder)
  }

  // Filter for access
  const visibleNav = navOrder.map(navID => {
    const nav = resolvedNav.find(n => n.id === navID)
    if (!nav) return null
    const navAccess = nav.href ? evaluatePathAccess(nav.href, accessUser) : 'denied'
    if (!navAccess && import.meta.env.DEV && !permLoading && !assignment.loading) {
      console.error(`[Sidebar] Nav-item "${nav.label}" href "${nav.href}" is not registered in routeAccess.ts ROUTES`)
    }
    const visibleSub = (nav.subItems ?? []).map((sub): RenderSubItem | null => {
      // Capture href in a const so the non-null narrowing survives the intervening call
      // and the returned element provably carries a string href (RenderSubItem).
      const href = sub.href
      if (!href) return null
      const subAccess = evaluatePathAccess(href, accessUser)
      if (subAccess !== 'ok' && import.meta.env.DEV && !permLoading && !assignment.loading) {
        console.error(`[Sidebar] Sub-item "${sub.label}" href "${href}" is not registered in routeAccess.ts ROUTES`)
      }
      return subAccess === 'ok' ? { ...sub, href } : null
    }).filter(sub => sub !== null)
    if (navAccess === 'ok' && nav.href) {
      return { ...nav, href: nav.href, subItems: visibleSub }
    }
    if (visibleSub.length > 0) {
      const fallbackSub = visibleSub[0]
      if (fallbackSub.preserveSub === false)
        visibleSub.shift()
      return {
        ...nav,
        label: fallbackSub.updateLabel ? fallbackSub.label : nav.label,
        href: fallbackSub.href,
        navState: fallbackSub.navState,
        subItems: visibleSub
      }
    }
    return null
  }).filter(nav => nav !== null)
  const visibleFooterNav = FOOTER_NAV.map(item => {
    // Narrow href here so survivors carry a non-optional href (RenderNavItem).
    if (!item.href) return null
    return evaluatePathAccess(item.href, accessUser) === 'ok' ? { ...item, href: item.href } : null
  }).filter(item => item !== null)

  // Auto-expand when navigating to a section with visible sub-items; leaf items leave expansion unchanged.
  const [expanded, setExpanded] = useState<string | null>(
    activeMatch?.navId && visibleNav.find(n => n.id === activeMatch.navId)?.subItems?.length
      ? activeMatch.navId : null
  )
  useEffect(() => {
    if (activeMatch?.navId && visibleNav.find(n => n.id === activeMatch.navId)?.subItems?.length)
      setExpanded(activeMatch.navId)
  }, [activeMatch?.navId, permLoading, assignment.loading])
  const toggle = (id: string) => setExpanded(prev => prev === id ? null : id)

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  )

  // Skip rendering until permissions and assignment are ready
  if (permLoading || assignment.loading) return null

  // Build element tree
  return (
    <nav
      aria-label="Sidebar"
      className="flex flex-col h-full"
      style={{ backgroundColor: 'var(--color-sidebar-bg, #111827)' }}
    >
      <div style={{ paddingTop: 'max(env(safe-area-inset-top, 0px) + 12px, 16px)' }} className="px-4 pb-4 mb-2 flex items-center gap-3">
        <img
          src={orgLogoUrl ?? brand.logoUrl}
          alt=""
          className="w-10 h-10 rounded-full object-contain bg-white p-0.5 shrink-0"
        />
        <div className="min-w-0">
          <h1 className="text-sm font-bold text-white leading-tight">{org?.dba ?? org?.name ?? brand.companyName}</h1>
          <p className="text-xs text-gray-500">Field Ops</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-3 space-y-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={navOrder} strategy={verticalListSortingStrategy}>
            {visibleNav.map(item => (
              <SortableNavItem
                key={item.id}
                item={item}
                activeMatch={activeMatch}
                expanded={expanded}
                toggle={toggle}
                onNavigate={onNavigate}
                badges={{ 'encounters': unsignedCounts }}
                chatUnread={chatUnread}
                isRainbow={isRainbow}
                sidebarText={sidebarText}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <div className="mt-2 px-3 py-2 space-y-0.5 border-t border-white/[0.06] mx-2 rounded-t-lg">
        <div className="flex items-center justify-between px-2 py-1">
          <div className="text-xs truncate" style={{ color: sidebarText.muted }}>
            {assignment.employee?.name} · {assignment.unit?.name || 'No unit'}
          </div>
          <Link
            to="/notifications"
            aria-label={notifyCount > 0 ? `Notifications - ${notifyCount} unread` : 'Notifications'}
            aria-current={pathname.startsWith('/notifications') ? 'page' : undefined}
            className={`relative p-0.5 rounded-md transition-colors ${pathname.startsWith('/notifications') ? '' : 'hover:bg-white/[0.06]'}`}
            style={{ color: pathname.startsWith('/notifications') ? 'var(--color-primary, #f87171)' : sidebarText.inactive }}
          >
            <span aria-hidden="true" className={`w-4 h-4 flex items-center justify-center ${notifyCount > 0 ? 'opacity-100 text-white' : 'opacity-60'}`}><SidebarIcon name="bell" /></span>
            {notifyCount > 0 && (
              <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold leading-none">
                {notifyCount > 9 ? '9+' : notifyCount}
              </span>
            )}
          </Link>
        </div>
        {visibleFooterNav.map(item => {
            const isActive = activeMatch?.navId === item.id
            return (
              <Link
                key={item.id}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                style={{ color: isActive ? 'var(--color-primary, #f87171)' : sidebarText.inactive }}
                className={`flex items-center gap-2 py-1 px-2 rounded-lg text-xs transition-all duration-150 ${isActive ? '' : 'hover:bg-white/[0.04]'}`}
              >
                <span aria-hidden="true" className="w-5 h-5 shrink-0 flex items-center justify-center opacity-50" style={isRainbow ? { color: RAINBOW_ICON_COLORS[item.icon], opacity: 1 } : {}}>
                  <SidebarIcon name={item.icon} />
                </span>
                {item.label}
              </Link>
            )
          })
        }
        <button
          onClick={async () => {
            const { signOut } = await import('@/contexts/UserContext')
            await signOut()
            window.location.href = '/login'
          }}
          style={{ color: sidebarText.muted }} className="flex items-center gap-2 py-1 px-2 rounded-lg text-xs hover:bg-white/[0.04] transition-all duration-150 w-full"
        >
          <span aria-hidden="true" className="w-5 h-5 shrink-0 flex items-center justify-center opacity-50" style={isRainbow ? { color: RAINBOW_ICON_COLORS.logout, opacity: 1 } : {}}><SidebarIcon name="logout" /></span> Sign Out
        </button>
        <p className="text-[10px] text-center pt-0.5" style={{ color: sidebarText.muted }}>{brand.appBrand} v{import.meta.env.VITE_APP_VERSION}</p>
      </div>
    </nav>
  )
}
